import React, { useState, useMemo } from 'react';
import type { Table, TableStatus } from '../types';
import QrCodeIcon from '../components/icons/QrCodeIcon';
import QrCodeModal from '../components/qr/QrCodeModal';
import MagnifyingGlassIcon from '../components/icons/MagnifyingGlassIcon';

interface QrManagementPageProps {
  tables: Table[];
  onSimulateScan: (table: Table) => void;
}

const QrManagementPage: React.FC<QrManagementPageProps> = ({ tables, onSimulateScan }) => {
  const [modalTable, setModalTable] = useState<Table | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TableStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;

  // Filter tables - show all tables (including merged ones) for QR code generation
  // Each table should have its own QR code regardless of merge status
  const filteredTables = useMemo(() => {
    let filtered = [...tables]; // Show all tables, don't filter out merged tables
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchLower)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    
    return filtered;
  }, [tables, searchTerm, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredTables.length / itemsPerPage);
  const paginatedTables = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTables.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTables, currentPage]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const getStatusColor = (status: TableStatus) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'occupied':
        return 'bg-yellow-100 text-yellow-800';
      case 'reserved':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">Table QR Code Management</h1>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tables by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TableStatus | 'all')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="reserved">Reserved</option>
            </select>
            
            {/* Results count */}
            <div className="flex items-center text-sm text-gray-600">
              Showing {paginatedTables.length} of {filteredTables.length} tables
            </div>
          </div>
        </div>

        {/* Tables Grid */}
        {paginatedTables.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {paginatedTables.map(table => (
                <div key={table.id} className="bg-white p-4 rounded-lg shadow-md text-center">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-gray-800">{table.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(table.status)}`}>
                      {table.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">Capacity: {table.capacity}</p>
                  <button 
                    onClick={() => setModalTable(table)}
                    className="w-full bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 flex items-center justify-center"
                  >
                    <QrCodeIcon className="w-5 h-5 mr-2" />
                    View QR Code
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No tables found matching your criteria.</p>
          </div>
        )}
      </main>
      {modalTable && (
        <QrCodeModal
          table={modalTable}
          onClose={() => setModalTable(null)}
          onSimulateScan={() => {
            onSimulateScan(modalTable);
            setModalTable(null);
          }}
        />
      )}
    </div>
  );
};

export default QrManagementPage;
